import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {Alert, Link} from '@forecast-it/design-system';
import {AiCreditBanner, AiCreditBannerProvider} from './AiCreditBanner';
import {BannerWrapper} from './BannerWrapper';

const DISMISSED_KEY = 'genAiDisabledBannerDismissed';
const SUPPORT_EMAIL = 'support@forecast.app';
const GEN_AI_DISABLED_KEY = 'gen_ai_disabled';

type BannerProviderProps = {
	companyId?: string | null;
	flags?: ReadonlyArray<{readonly key: string} | null> | null;
	children: React.ReactNode;
};

const readDismissed = (): boolean => {
	try {
		return sessionStorage.getItem(DISMISSED_KEY) === 'true';
	} catch {
		return false;
	}
};

const writeDismissed = (): void => {
	try {
		sessionStorage.setItem(DISMISSED_KEY, 'true');
	} catch {
		// Losing the record only costs a repeat of the banner.
	}
};

const clearDismissed = (): void => {
	try {
		sessionStorage.removeItem(DISMISSED_KEY);
	} catch {
		// Nothing to do.
	}
};

type BannerContextValue = {
	visible: boolean;
	onDismiss: () => void;
	slotted: boolean;
	setSlotted: (v: boolean) => void;
};

const BannerContext = createContext<BannerContextValue | null>(null);

const BannerAlert = (): React.ReactElement | null => {
	const ctx = useContext(BannerContext);
	const {formatMessage} = useIntl();
	if (!ctx || !ctx.visible) return null;

	const message = (
		<>
			{formatMessage(
				{id: 'gen_ai.disabled_banner'},
				{email: <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>}
			)}
			.
		</>
	);

	return (
		<BannerWrapper data-cy="gen-ai-disabled-banner">
			<Alert type={Alert.TYPE.ERROR} text={message as unknown as string} onDismiss={ctx.onDismiss} />
		</BannerWrapper>
	);
};

/**
 * Provides banner state to the component tree. Wrap around the nav + content area
 * so both the default full-bleed renderer and any GenAiBannerSlot can access state.
 * Also provides the AI credit notices, which are suppressed while gen_ai_disabled is active.
 */
export const GenAiBannerProvider = ({companyId, flags, children}: BannerProviderProps): React.ReactElement => {
	const wasDisabled = useRef(false);
	const [visible, setVisible] = useState(false);
	const [slotted, setSlotted] = useState(false);
	const disabled = useMemo(() => (flags ?? []).some(flag => flag?.key === GEN_AI_DISABLED_KEY), [flags]);

	useEffect(() => {
		if (!disabled) {
			if (wasDisabled.current) {
				setVisible(false);
				clearDismissed();
			}
			wasDisabled.current = false;
			return;
		}

		if (wasDisabled.current) return;
		wasDisabled.current = true;

		if (readDismissed()) return;

		setVisible(true);
	}, [disabled]);

	const onDismiss = useCallback(() => {
		writeDismissed();
		setVisible(false);
	}, []);

	const contextValue = useMemo(() => ({visible, onDismiss, slotted, setSlotted}), [visible, onDismiss, slotted]);

	return (
		<BannerContext.Provider value={contextValue}>
			<AiCreditBannerProvider companyId={companyId} suppressed={disabled}>
				{children}
			</AiCreditBannerProvider>
		</BannerContext.Provider>
	);
};

/**
 * Renders the banner inside a sidebar page's content column instead of full-bleed.
 * Mount this inside the content area of pages that have a secondary sidebar.
 * When mounted, the default full-bleed GenAiDisabledBanner hides.
 */
export const GenAiBannerSlot = (): React.ReactElement | null => {
	const ctx = useContext(BannerContext);

	useEffect(() => {
		if (!ctx) return;
		ctx.setSlotted(true);
		return () => ctx.setSlotted(false);
	}, [ctx]);

	if (!ctx) return null;
	return (
		<>
			<BannerAlert />
			<AiCreditBanner />
		</>
	);
};

/**
 * Default full-bleed banner. Renders below the top nav when gen_ai_disabled is active,
 * and the AI credit notice when the account is near or past its allowance.
 * Hides when a GenAiBannerSlot is mounted (sidebar pages render the slot instead).
 * Must be a descendant of GenAiBannerProvider.
 */
const GenAiDisabledBanner = (): React.ReactElement | null => {
	const ctx = useContext(BannerContext);
	if (!ctx || ctx.slotted) return null;
	return (
		<>
			<BannerAlert />
			<AiCreditBanner />
		</>
	);
};

export default GenAiDisabledBanner;
